<template>
  <div class="card mb-4">
    <div class="card-body">
      <div class="d-flex justify-content-between align-items-center">
        <div>
          <h5 class="card-title mb-2">Grexel Actions</h5>
          <p class="text-muted mb-0">
            <span v-if="totalRecordCount > 0">
              {{ totalRecordCount }} record(s) selected
            </span>
            <span v-else>No records selected</span>
          </p>
        </div>
        <div class="d-flex gap-2">
          <button
            class="btn btn-primary"
            :disabled="totalRecordCount === 0 || isSendingOrProcessing"
            @click="handleSendToGrexel"
          >
            <span
              v-if="isSendingOrProcessing"
              class="spinner-border spinner-border-sm me-2"
            ></span>
            <font-awesome-icon v-else icon="fas fa-upload" class="me-2" />
            {{ sendButtonText }}
            <span
              v-if="totalRecordCount > 0 && !isSendingOrProcessing"
              class="ms-1"
            >
              ({{ totalRecordCount }})
            </span>
          </button>
          <button
            class="btn btn-outline-primary"
            :disabled="isFetching"
            @click="handleFetchFromGrexel"
          >
            <span
              v-if="isFetching"
              class="spinner-border spinner-border-sm me-2"
            ></span>
            <font-awesome-icon v-else icon="fas fa-sync" class="me-2" />
            Fetch from Grexel
          </button>
        </div>
      </div>

      <!-- Send Results -->
      <div v-if="sendToGrexelResult" class="mt-3">
        <div class="alert alert-success alert-dismissible fade show">
          <strong>Send Complete:</strong>
          {{ sendToGrexelResult.total_sent }} sent,
          {{ sendToGrexelResult.total_skipped }} skipped,
          {{ sendToGrexelResult.total_errors }} errors
          <button
            type="button"
            class="btn-close"
            @click="resetSendToGrexel"
          ></button>
        </div>
      </div>

      <!-- Send Error -->
      <div v-if="sendToGrexelErrorMessage" class="mt-3">
        <div class="alert alert-danger alert-dismissible fade show">
          <strong>Send Failed:</strong> {{ sendToGrexelErrorMessage }}
          <button
            type="button"
            class="btn-close"
            @click="resetSendToGrexel"
          ></button>
        </div>
      </div>

      <!-- Fetch Results -->
      <div v-if="fetchResult" class="mt-3">
        <div class="alert alert-info alert-dismissible fade show" role="alert">
          <strong>Fetch Complete:</strong>
          {{ fetchResult.total_updated }} updated ({{
            fetchResult.total_completed
          }}
          completed, {{ fetchResult.total_rejected }} rejected)<span
            v-if="fetchResult.total_pending_approval > 0"
          >
            • {{ fetchResult.total_pending_approval }} pending approval </span
          ><span v-if="fetchResult.total_approved > 0">
            • {{ fetchResult.total_approved }} approved
          </span>
          <button
            type="button"
            class="btn-close"
            aria-label="Close"
            @click="fetchResult = null"
          ></button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { sumBy } from "lodash-es";
import { computed, ref, watch } from "vue";
import { fetchFromGrexel } from "./api";
import { useSendToGrexel } from "./composables/useSendToGrexel";
import type { CompanyRecords, FetchFromGrexelResponse } from "./types";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";

// Props
const props = defineProps<{
  companyRecords: CompanyRecords[];
  year: number;
  month: number;
}>();

// Computed: total record count across all companies
const totalRecordCount = computed(() =>
  sumBy(props.companyRecords, (cr) => cr.record_ids.length)
);

// Emits
const emit = defineEmits<{
  "grexel:sent": [];
  "grexel:fetched": [];
}>();

// Global error handler
const { globalErrorHandler } = useGlobalHandlers();

// Send to Grexel composable
const {
  state: sendToGrexelState,
  result: sendToGrexelResult,
  error: sendToGrexelError,
  errorMessage: sendToGrexelErrorMessage,
  send: sendToGrexelAction,
  reset: resetSendToGrexel,
} = useSendToGrexel();

// Computed
const isSendingOrProcessing = computed(
  () =>
    sendToGrexelState.value === "starting" ||
    sendToGrexelState.value === "processing"
);

const sendButtonText = computed(() => {
  if (sendToGrexelState.value === "starting") {
    return "Starting...";
  }
  if (sendToGrexelState.value === "processing") {
    return "Sending...";
  }
  return "Send to Grexel";
});

// Watch for send completion to emit event and refresh table
watch(sendToGrexelState, (newState) => {
  if (newState === "success") {
    emit("grexel:sent");
  } else if (newState === "failed" && sendToGrexelError.value) {
    // A raw error means the send never started; the inline alert only shows the generic message,
    // so also route it to the global handler, which surfaces the backend's error code. Task-level
    // failures carry no raw error and stay on the inline alert alone.
    globalErrorHandler(sendToGrexelError.value);
  }
});

// State for fetch
const isFetching = ref(false);
const fetchResult = ref<FetchFromGrexelResponse | null>(null);

const handleSendToGrexel = async () => {
  if (totalRecordCount.value === 0) return;
  // Both outcomes, a failed start included, are reported by the sendToGrexelState watcher.
  await sendToGrexelAction(props.companyRecords);
};

const handleFetchFromGrexel = async () => {
  isFetching.value = true;
  fetchResult.value = null;

  try {
    const result = await fetchFromGrexel(props.year, props.month);
    fetchResult.value = result;

    // Emit success event to trigger table refresh
    emit("grexel:fetched");
  } catch (error: unknown) {
    globalErrorHandler(error);
  } finally {
    isFetching.value = false;
  }
};
</script>

<style scoped lang="scss">
.card-title {
  margin-bottom: 0;
}

.alert {
  margin-bottom: 0;
}
</style>
